/** Age / risk colour helpers for Cash & stock intelligence tables. */

const FRESH_MAX_DAYS = 30;
const CRITICAL_AT_DAYS = 90;

const lerp = (a, b, t) => a + (b - a) * t;

const rgb = (r, g, b) => `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;

const rgba = (r, g, b, a) => `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${a})`;

/**
 * Shelf idle age — green ≤30 days, shifts toward red as days increase.
 * @param {number|null|undefined} daysSinceSale
 */
export function getStockIdleAgeVisual(daysSinceSale) {
  if (daysSinceSale == null || daysSinceSale === undefined) {
    return {
      color: "#DC2626",
      rowBg: rgba(220, 38, 38, 0.12),
      rowClass: "owner-wc-age-row--critical",
      badgeClass: "owner-wc-status--critical",
      barPercent: 100,
      label: "Never sold",
    };
  }

  const days = Number(daysSinceSale);
  if (Number.isNaN(days) || days <= FRESH_MAX_DAYS) {
    const freshRatio = Math.max(0, Math.min(1, days / FRESH_MAX_DAYS));
    return {
      color: "#16A34A",
      rowBg: rgba(22, 163, 74, lerp(0.06, 0.1, freshRatio)),
      rowClass: "owner-wc-age-row--fresh",
      badgeClass: "owner-wc-status--ok",
      barPercent: Math.max(12, Math.round(freshRatio * 35)),
      label: days <= FRESH_MAX_DAYS ? "Fresh stock" : "Fresh",
    };
  }

  const t = Math.min(1, (days - FRESH_MAX_DAYS) / (CRITICAL_AT_DAYS - FRESH_MAX_DAYS));
  const r = lerp(22, 220, t);
  const g = lerp(163, 38, t);
  const b = lerp(74, 38, t);

  let rowClass = "owner-wc-age-row--aging";
  let badgeClass = "owner-wc-status--warning";
  if (t >= 0.75) {
    rowClass = "owner-wc-age-row--critical";
    badgeClass = "owner-wc-status--critical";
  } else if (t >= 0.35) {
    rowClass = "owner-wc-age-row--warning";
  }

  return {
    color: rgb(r, g, b),
    rowBg: rgba(r, g, b, lerp(0.08, 0.14, t)),
    rowClass,
    badgeClass,
    barPercent: Math.round(35 + t * 65),
    label: t >= 0.75 ? "Stale stock" : t >= 0.35 ? "Aging stock" : "Watch shelf age",
  };
}

/**
 * Fuel days-of-cover vs target — ok / warning / critical from backend status.
 */
export function getFuelStatusRowClass(status) {
  const map = {
    ok: "owner-wc-fuel-row--ok",
    warning: "owner-wc-fuel-row--warning",
    critical: "owner-wc-fuel-row--critical",
  };
  return map[status] || "owner-wc-fuel-row--ok";
}

/**
 * Thin margin heat — lower margin % = redder row.
 * @param {number} marginPct
 */
export function getMarginHeatVisual(marginPct) {
  const pct = Number(marginPct);
  if (Number.isNaN(pct)) {
    return {
      color: "#64748B",
      rowBg: "transparent",
      rowClass: "",
      badgeClass: "",
    };
  }

  if (pct >= 12) {
    return {
      color: "#16A34A",
      rowBg: rgba(22, 163, 74, 0.06),
      rowClass: "owner-wc-margin-row--ok",
      badgeClass: "owner-wc-status--ok",
    };
  }

  if (pct >= 8) {
    return {
      color: "#D97706",
      rowBg: rgba(245, 166, 35, 0.08),
      rowClass: "owner-wc-margin-row--warning",
      badgeClass: "owner-wc-status--warning",
    };
  }

  const t = Math.min(1, Math.max(0, (8 - pct) / 8));
  const r = lerp(217, 220, t);
  const g = lerp(119, 38, t);
  const b = lerp(6, 38, t);

  return {
    color: rgb(r, g, b),
    rowBg: rgba(r, g, b, lerp(0.08, 0.14, t)),
    rowClass: t >= 0.5 ? "owner-wc-margin-row--critical" : "owner-wc-margin-row--warning",
    badgeClass: t >= 0.5 ? "owner-wc-status--critical" : "owner-wc-status--warning",
  };
}

/**
 * Credit overdue age — same green→red scale as shelf idle days.
 * @param {number} oldestUnpaidDays
 */
export function getCreditAgeVisual(oldestUnpaidDays) {
  return getStockIdleAgeVisual(Number(oldestUnpaidDays));
}
